<template>
  <div class="page">
    <div fluid class="container-fluid main">
      <div class="fixed-header">
        <div class="container">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7"></div>
            <div class="col-md-12 col-lg-4">
              <Header/>
            </div>
          </div>
        </div>
      </div>
      <div class="content" :class="{ scrolled: scrolled }" @scroll="handleScroll">
        <div class="container" v-if="!isIE">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-7 column-spacer"></div>
            <div class="col-md-12 col-lg-4 primary">
              <div class="form-container">
                <!-- Step 1: OTP Verification -->
                <div v-if="currentStep === 1">
                  <h2 class="heading">Reset password</h2>
                  <div class="step-subheading">
                    Reset your password below. To complete your password reset, a One-Time PIN (OTP) will be SMSed to you, together with your username reminder.
                  </div>
                  <ErrorBox v-if="alert.message" variant="inline" :message="alert.message" />
                  <form method="post" @submit.prevent="handleSubmit">
                    <div class="form-group">
                      <input
                        type="text"
                        pattern=".{5,13}"
                        maxlength="13"
                        title="Must be between 5 and 13 characters long"
                        v-model="idNumber"
                        id="idNumber"
                        name="idNumber"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !idNumber }"
                      />
                      <label for="idNumber">ID/Passport/Netstar account number</label>
                      <ValidationFeedback
                        :show="submitted && !idNumber"
                        message="ID/Passport/Netstar account number is required"
                      />
                    </div>
                    
                    <div class="form-group" v-show="!inOtpPhase">
                      <div class="password-input-container">
                        <input
                          ref="passwordInput"
                          :type="showPassword ? 'text' : 'password'"
                          autocomplete="new-password"
                          pattern=".{8,}"
                          title="At least 8 characters"
                          v-model="password"
                          id="password"
                          name="password"
                          class="form-control"
                          :class="{ 'is-invalid': submitted && !password }"
                        />
                        <button 
                          type="button"
                          class="password-toggle-btn"
                          @click="togglePassword"
                        >
                          {{ showPassword ? 'Hide' : 'Show' }}
                        </button>
                      </div>
                      <label for="password">New Password</label>
                      <ValidationFeedback 
                        :show="submitted && !password" 
                        message="New Password is required" 
                      />
                    </div>
                    
                    <div class="form-group" v-show="!inOtpPhase">
                      <div class="password-input-container">
                        <input
                          ref="confirmPasswordInput"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          autocomplete="new-password"
                          pattern=".{8,}"
                          title="At least 8 characters"
                          v-model="confirmPassword"
                          id="confirmPassword"
                          name="confirmPassword"
                          class="form-control"
                          :class="{ 'is-invalid': submitted && (!confirmPassword || !passwordsMatch) }"
                        />
                        <button 
                          type="button"
                          class="password-toggle-btn"
                          @click="toggleConfirmPassword"
                        >
                          {{ showConfirmPassword ? 'Hide' : 'Show' }}
                        </button>
                      </div>
                      <label for="confirmPassword">Confirm password</label>
                      <ValidationFeedback 
                        :show="submitted && (!confirmPassword || !passwordsMatch)" 
                        message="Passwords must match" 
                      />
                    </div>
                    
                    <div class="form-group" v-show="inOtpPhase">
                      <input
                        type="text"
                        pattern=".{5}"
                        maxlength="5"
                        title="Must be 5 characters"
                        v-model="otp"
                        id="otp"
                        name="otp"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !otp }"
                      />
                      <label for="otp">OTP</label>
                      <ValidationFeedback 
                        :show="submitted && !otp" 
                        message="OTP is required" 
                      />
                    </div>
                    <div class="footer" v-if="otpCellNumber">
                      <div class="info-box">
                        <p class="text">Enter the One Time PIN (OTP) sent to your mobile number (******{{ otpCellNumber }}) and then click on the ‘CONFIRM’ button to confirm the password change.</p>
                      </div>
                    </div>
                    
                    <div class="form-group buttons">
                      <button 
                        class="btn btn-submit" 
                        :class="{ active: isFormValid }"
                        :disabled="processing"
                        type="submit"
                      >
                        <span v-show="!processing">{{ inOtpPhase ? "Confirm" : "Submit" }}</span>
                        <Spinner v-show="processing" />
                      </button>
                    </div>
                  </form>
                  
                </div>
                
                <!-- Step 2: Success Confirmation -->
                <div v-else-if="currentStep === 2">
                  <h2 class="heading">Reset password</h2>
                  <div class="step-subheading reset-success">Password reset successfully.</div>
                  
                  <div class="form-group buttons step-2-buttons">
                    <router-link to="login" class="btn btn-login">Login</router-link>
                  </div>
                </div>
                <div class="buttons single-back">
                    <button 
                      class="btn btn-back"
                      @click="goToLogin"
                    >
                      <img src="~@/assets/images/left-chevron.svg" alt="Back" class="chevron-icon" />
                      Back
                    </button>
                  </div>
              </div>
            </div>
          </div>
        </div>
        <IncompatibleBrowser v-else />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~@/assets/scss/pages.scss";

.page {
  .content {
    > .container {

      .primary {
        .footer {
          margin-top: 0;
          .text {
            color: $new-brand-color-2;
            font-size: 12px;
            margin: 0;
            line-height: 1.5em;
            font-weight: 600;
          }
        }
      }
    }
  }
}

// Step-specific styles following RegisterPage.vue pattern
.step-subheading {
  font-weight: 600;
  line-height: 23px;
  margin-bottom: 32px;

  &.reset-success {
    color: $new-brand-color-2;
  }
}

.form-group.buttons {
  display: flex;
  gap: 12px;
  align-items: center;
  
  &.step-2-buttons {
    justify-content: flex-end;
    border-top: none;
    padding-top: 0;
  }

  .btn-login {
    background: $new-brand-color-1;
    color: white;
    font-weight: bold;
    width: 154px;
    font-size: 18px;
    text-transform: none;
    padding: 10px 40px;
    margin-top: 25px;
    margin-bottom: 15px;
    border-radius: 25px;
    transition: all 0.2s ease;
    height: $onboarding-button-height;
  }
}
</style>

<script>
import { mapState, mapActions } from "vuex";
import { alertService, userService } from "@/services";
import Header from "@/pages/components/Header";
import IncompatibleBrowser from "@/components/IncompatibleBrowser";
import ValidationFeedback from "@/components/ValidationFeedback";
import { helper } from "@/helper";

export default {
  components: {
    Header,
    IncompatibleBrowser,
    ValidationFeedback,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetErrors();
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.resetErrors();
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.resetErrors();
    next();
  },
  data() {
    return {
      currentStep: 1,
      submitted: false,
      processing: false,
      success: false,
      idNumber: "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      otp: "",
      token: "",
      otpCellNumber: "",
      passwordsMatch: false,
      inOtpPhase: false,
      scrolled: false,
      resetPasswordError: "",
    };
  },
  computed: {
    ...mapState("account", ["status"]),
    ...mapState(["alert"]),
    isIE() {
      return /*@cc_on!@*/ false || !!document.documentMode;
    },
    isFormValid() {
      if (this.inOtpPhase) {
        return this.otp && this.token;
      }
      return this.idNumber && this.password && this.confirmPassword && this.passwordsMatch;
    },
  },
  methods: {
    ...mapActions("alert", {
      clearAlert: "clear",
    }),
    resetErrors() {
      this.resetPasswordError = "";
      this.submitted = false;
      this.processing = false;
      this.clearAlert();
    },
    handleScroll(event) {
      this.scrolled = event.target.scrollTop > 5;
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
      this.$nextTick(() => {
        this.$refs.passwordInput?.focus();
      });
    },
    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
      this.$nextTick(() => {
        this.$refs.confirmPasswordInput?.focus();
      });
    },
    goToLogin() {
      this.$router.push('/login');
    },
    handleSubmit(e) {
      this.clearAlert();
      this.submitted = true;
      this.resetPasswordError = ""; // Clear any previous errors
      const { idNumber, password, confirmPassword, passwordsMatch, otp, token, inOtpPhase } = this;

      if (idNumber && password && confirmPassword && passwordsMatch) {
        if (inOtpPhase && (!otp || !token)) {
          return;
        }
        this.processing = true;
        userService
          .resetPassword({
            idNumber: idNumber,
            password: password,
            otp: otp,
            token: token,
            origin: helper.getOriginInfo(),
          })
          .then((data) => {
            this.processing = false;
            this.submitted = false;
            this.token = data.Token;
            this.otpCellNumber = data.OTPCellNumber;
            this.inOtpPhase = !data.OTPVerified;
            if (data.TransactionComplete) {
              this.currentStep = 2;
              this.success = true;
            }
          })
          .catch((error) => {
            this.processing = false;
            this.submitted = false;
            alertService.showErrorAlert(error);
          });
      }
    },
  },
  watch: {
    password(value) {
      this.resetPasswordError = ""; // Clear any previous errors when user starts typing
      this.passwordsMatch = this.confirmPassword == value;
    },
    confirmPassword(value) {
      this.resetPasswordError = ""; // Clear any previous errors when user starts typing
      this.passwordsMatch = this.password == value;
    },
  },
};
</script>
